import { useMemo, useState, useEffect, useCallback } from 'react';
import useExpandCollapseAllState from 'modules/common/hooks/useExpandCollapseAllState';
import { SmartDiaryEntry } from '../../type';

type useSmartDiaryAccordianStateProps = {
  expandCollapseAllId: string;
  diariesMap: Record<string, SmartDiaryEntry[]>;
};

export const useSmartDiaryAccordianState = ({
  diariesMap,
  expandCollapseAllId
}: useSmartDiaryAccordianStateProps): {
  smartDiaryAccordianState: Record<string, boolean>;
  onExpandCollapse: (date: string) => void;
  onExpandCollapseAll: (state: boolean) => void;
} => {
  const { expandAllState } = useExpandCollapseAllState(expandCollapseAllId);

  const expandedStateMapDefaultValues = useMemo(
    () =>
      Object.keys(diariesMap).reduce((acc, value) => {
        return {
          ...acc,
          [value]: Boolean(expandAllState)
        };
      }, {} as Record<string, boolean>),
    [diariesMap, expandAllState]
  );

  const [accordianStateMap, setAccordianStateMap] = useState<
    Record<string, boolean>
  >(expandedStateMapDefaultValues);

  useEffect(() => {
    setAccordianStateMap(expandedStateMapDefaultValues);
  }, [expandedStateMapDefaultValues]);

  const onExpandCollapse = useCallback(
    (date: string) => {
      const oldValue = accordianStateMap[date];

      setAccordianStateMap({
        ...accordianStateMap,
        [`${date}`]: !oldValue
      });
    },
    [setAccordianStateMap, accordianStateMap]
  );

  const onExpandCollapseAll = useCallback(
    (state: boolean) => {
      setAccordianStateMap((previousMap) =>
        Object.keys(previousMap).reduce((acc, value) => {
          return {
            ...acc,
            [value]: Boolean(state)
          };
        }, {} as Record<string, boolean>)
      );
    },
    [setAccordianStateMap]
  );

  return {
    smartDiaryAccordianState: accordianStateMap,
    onExpandCollapse,
    onExpandCollapseAll
  };
};
